import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Invoice from "../invoice/Invoice";
import DocumentUpload from "../../common/DocumentUpload";
import ToDoList from "../dashboard/ToDoList";
import { useParams } from "react-router-dom";
import CustomerProfileData from "./CustomerProfileData";
import CustomerApp_process from "./CustomerApp_process";
import InvoiceTable from "../invoice/InvoiceTable";
import ServiceTaken from "./ServiceTaken";
import { getInvoices } from "../../../services/invoice.services";
import { setInvoices } from "../../../state/reducers/invoiceSlice";

const CustomerProfile = () => {
  const [viewInvoice, setViewInvoice] = useState(null);
  const { id } = useParams();
  const customerDetail = useSelector(
    (state) => state.customer.customerDetail || []
  );
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const leads = useSelector((state) => state.leads.leads);
  const [activeTab, setActiveTab] = useState("Profile");
  const childLeads = useSelector((state) => state.leads.childLeads);
  const dispatch = useDispatch();
  // For smaller screens - mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getAllInvoices = async () => {
    try {
      const res = await getInvoices();
      dispatch(setInvoices(res.data));
      if (viewInvoice) {
        setViewInvoice(
          res.data.filter((item) => item.id === viewInvoice.id)[0]
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Tabs and their associated components
  const tabs = [
    {
      name: "Profile",
      component: customerDetail ? <CustomerProfileData /> : "loading...",
    },
    {
      name: "Invoices",
      component: (
        <InvoiceTable
          customerId={id}
          setViewInvoice={setViewInvoice}
          viewInvoice={viewInvoice}
          getAllInvoices={getAllInvoices}
        />
      ),
    },
    { name: "Services Taken", component: <ServiceTaken /> },
  ];

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Helper function to render field with proper overflow handling
  const renderField = (label, value, isArray = false) => {
    const displayValue = () => {
      if (!value || (isArray && (!value.length || value.length === 0))) {
        return (
          <span className="text-gray-400 italic text-sm">Not provided</span>
        );
      }

      if (isArray) {
        return (
          <span className="text-sm break-words">
            {value.map((item, index) => (
              <span key={index}>
                {item}
                {index < value.length - 1 ? ", " : ""}
              </span>
            ))}
          </span>
        );
      }

      return <span className="text-sm break-words">{value}</span>;
    };

    return (
      <div className="mb-3 pb-2 border-b border-gray-100 last:border-b-0">
        <div className="flex flex-col space-y-1">
          <span className="font-semibold text-gray-700 text-sm">{label}:</span>
          <div className="min-w-0 overflow-hidden">{displayValue()}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Main Content */}
      <div
        className={`flex-1 transition-all duration-300 ease-in-out ${
          isSideBarOpen ? "" : "md:ml-10 lg:ml-0"
        }`}
      >
        {/* Customer Header Card */}
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="bg-white border-2 border-purple-600 rounded-lg shadow-lg mx-auto">
            {/* Top Section */}
            <div className="border-b-2 border-gray-200 p-4 sm:p-6">
              <div className="flex flex-col space-y-2">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 break-words">
                  {customerDetail.name || "Customer Name"}
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 break-words">
                  Word of Mouth:{" "}
                  {leads && leads.length > 0
                    ? leads.find((lead) => lead.id === customerDetail.lead)
                        ?.parent_info?.reference_source ||
                      leads.find((lead) => lead.id === customerDetail.lead)
                        ?.parent_info.reference_name_id ||
                      leads.find((lead) => lead.id === customerDetail.lead)
                        ?.parent_info.other_reference_source
                    : childLeads && childLeads.length > 0
                    ? childLeads.find((lead) => lead.id === customerDetail.lead)
                        ?.parent_info?.reference_source ||
                      childLeads.find((lead) => lead.id === customerDetail.lead)
                        ?.parent_info.reference_name_id ||
                      childLeads.find((lead) => lead.id === customerDetail.lead)
                        ?.parent_info.other_reference_source
                    : "N/A"}
                </p>
              </div>
            </div>

            {/* Details Section - Responsive Grid */}
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* First Column */}
                <div className="bg-gray-50 rounded-lg p-4 min-w-0">
                  <h3 className="font-semibold text-gray-800 mb-4 text-base border-b pb-2">
                    Personal Information
                  </h3>
                  {renderField("Student Name", customerDetail.name)}
                  {renderField(
                    "Mobile Number",
                    customerDetail.phone ? `+${customerDetail.phone}` : null
                  )}
                  {renderField("Email ID", customerDetail.email)}
                  {renderField("Location", customerDetail.city)}
                </div>

                {/* Second Column */}
                <div className="bg-gray-50 rounded-lg p-4 min-w-0">
                  <h3 className="font-semibold text-gray-800 mb-4 text-base border-b pb-2">
                    Academic Information
                  </h3>
                  {renderField(
                    "Countries",
                    customerDetail.preferred_countries,
                    true
                  )}
                  {renderField("Intake", customerDetail.intake)}
                  {renderField(
                    "Intended Degree",
                    customerDetail.intended_degree
                  )}
                  {renderField(
                    "Intended Major",
                    customerDetail.intended_master
                  )}
                </div>

                {/* Third Column */}
                <div className="bg-gray-50 rounded-lg p-4 min-w-0">
                  <h3 className="font-semibold text-gray-800 mb-4 text-base border-b pb-2">
                    Service Information
                  </h3>
                  {renderField(
                    "Services Taken",
                    customerDetail.subservice_name,
                    true
                  )}
                  {renderField("Branch", customerDetail.branch)}
                  {renderField("Current Status", customerDetail.status)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Responsive Tabs and Content */}
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="bg-white rounded-lg shadow-lg mx-auto">
            <div className="flex flex-col lg:flex-row min-h-96">
              {/* Mobile Tab Dropdown */}
              <div className="lg:hidden w-full p-4 border-b relative z-20">
                <button
                  onClick={toggleMobileMenu}
                  className="w-full flex items-center justify-between bg-purple-600 text-white px-4 py-3 rounded-lg shadow-md hover:bg-purple-700 transition-colors"
                >
                  <span className="font-medium">{activeTab}</span>
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${
                      mobileMenuOpen ? "transform rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </button>

                {/* Mobile Menu Dropdown */}
                {mobileMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 bg-black bg-opacity-25 z-10"
                      onClick={() => setMobileMenuOpen(false)}
                    ></div>

                    {/* Dropdown Menu */}
                    <div className="absolute z-20 w-full bg-white shadow-xl rounded-lg mt-2 border">
                      <ul className="py-2">
                        {tabs.map((tab) => (
                          <li
                            key={tab.name}
                            onClick={() => {
                              setActiveTab(tab.name);
                              setMobileMenuOpen(false);
                            }}
                            className={`cursor-pointer px-4 py-3 text-sm transition-colors ${
                              activeTab === tab.name
                                ? "bg-purple-100 text-purple-700 font-semibold border-r-4 border-purple-600"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {tab.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>

              {/* Desktop Sidebar - Hidden on mobile */}
              <div className="hidden lg:block lg:w-64 bg-gray-50 border-r">
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-4">
                    Navigation
                  </h3>
                  <ul className="space-y-2">
                    {tabs.map((tab) => (
                      <li
                        key={tab.name}
                        onClick={() => setActiveTab(tab.name)}
                        className={`cursor-pointer px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          activeTab === tab.name
                            ? "bg-purple-600 text-white shadow-md transform translate-x-1"
                            : "bg-white text-gray-700 hover:bg-gray-100 hover:shadow-sm border"
                        }`}
                      >
                        {tab.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                <div className="p-4 sm:p-6 h-full">
                  <div className="h-full overflow-auto">
                    {/* Render the component for the active tab */}
                    {tabs.find((tab) => tab.name === activeTab)?.component || (
                      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <div className="text-center">
                          <svg
                            className="w-16 h-16 text-gray-400 mx-auto mb-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <p className="text-gray-600 font-medium">
                            Select a tab to view content
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
